import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../services/firebase";

export default function AdminPanel({ user }) {
  const [users, setUsers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [requests, setRequests] = useState([]);
  const [overdueItems, setOverdueItems] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const navigate = useNavigate();

  // 🔒 Redirect non-admins
  useEffect(() => {
    if (!user || user.role !== "admin") {
      navigate("/login");
    }
  }, [user, navigate]);

  // 🔄 Real-time Firestore listeners
  useEffect(() => {
    if (!user || user.role !== "admin") return;

    // Users
    const unsubUsers = onSnapshot(collection(db, "users"), (snapshot) => {
      setUsers(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    // Assets
    const unsubAssets = onSnapshot(collection(db, "assets"), (snapshot) => {
      setAssets(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    // Requests
    const unsubRequests = onSnapshot(collection(db, "requests"), (snapshot) => {
      setRequests(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    return () => {
      unsubUsers();
      unsubAssets();
      unsubRequests();
    };
  }, [user]);

  // Calculate overdue items and notifications
  useEffect(() => {
    const now = new Date();
    const overdue = [];
    const notifs = [];

    // Check for overdue assets (borrowed items past return date)
    requests.forEach(req => {
      if (req.status === "approved" && req.returnDate) {
        const returnDate = new Date(req.returnDate);
        if (returnDate < now) {
          overdue.push({
            type: "overdue_asset",
            id: req.id,
            assetName: req.assetName,
            borrower: req.requesterName,
            returnDate: returnDate,
            daysOverdue: Math.floor((now - returnDate) / (1000 * 60 * 60 * 24))
          });
        }
      }
    });

    // Check for assets nearing depreciation
    assets.forEach(asset => {
      if (asset.depreciationDate) {
        const depDate = new Date(asset.depreciationDate);
        const daysUntilDep = Math.floor((depDate - now) / (1000 * 60 * 60 * 24));
        if (daysUntilDep <= 30 && daysUntilDep > 0) {
          notifs.push({
            type: "depreciation_warning",
            id: asset.id,
            assetName: asset.name,
            daysUntilDepreciation: daysUntilDep,
            depreciationDate: depDate
          });
        } else if (daysUntilDep <= 0) {
          notifs.push({
            type: "depreciated",
            id: asset.id,
            assetName: asset.name,
            depreciationDate: depDate
          });
        }
      }
    });

    setOverdueItems(overdue);
    setNotifications(notifs);
  }, [assets, requests]);

  if (!user || user.role !== "admin") return null;

  // 🧮 Derived stats
  const availableAssets = assets.filter((a) => a.status === "available").length;
  const inUseAssets = assets.filter((a) => a.status === "in-use").length;
  const maintenanceAssets = assets.filter((a) => a.status === "maintenance").length;
  const depreciatedAssets = assets.filter((a) => a.status === "depreciated").length;
  const pendingRequests = requests.filter((r) => r.status === "pending").length;

  return (
    <div className="bg-gradient-to-br from-neutral-50 via-primary-50/30 to-neutral-100 p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 md:mb-8 animate-fade-in">
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-800 mb-2">
          Welcome back, {user?.name || "Administrator"}
        </h1>
        <p className="text-neutral-600 text-base md:text-lg">Here's what's happening with your asset management system today.</p>
      </div>

      {/* Dashboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard title="Total Assets" value={assets.length} icon="📦" bgColor="from-primary-500 to-primary-600" />
        <StatCard title="Total Users" value={users.length} icon="👥" bgColor="from-success-500 to-success-600" />
        <StatCard title="Pending Requests" value={pendingRequests} icon="⏳" bgColor="from-warning-500 to-warning-600" />
      </div>

      {/* Two-column section: Recent Assets + System Overview */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Recent Assets */}
        <div className="glass-card p-6 animate-scale-in">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-primary-500/20 rounded-lg flex items-center justify-center mr-3">
              <span className="text-primary-600 text-sm">📦</span>
            </div>
            <h3 className="text-xl font-semibold text-neutral-800">Recent Assets</h3>
          </div>
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {assets.slice(0, 5).map((asset) => (
              <div
                key={asset.id}
                className="flex justify-between items-center p-4 bg-surface-100/50 rounded-xl hover:bg-surface-200/50 transition-colors duration-200"
              >
                <div>
                  <span className="font-medium text-neutral-800">{asset.name}</span>
                  <p className="text-xs text-neutral-500 mt-1">{asset.category || 'No category'}</p>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${
                    asset.status === "available"
                      ? "bg-success-100 text-success-800"
                      : asset.status === "in-use"
                      ? "bg-info-100 text-info-800"
                      : "bg-warning-100 text-warning-800"
                  }`}
                >
                  {asset.status}
                </span>
              </div>
            ))}
            {assets.length === 0 && (
              <p className="text-neutral-500 text-center py-8">No assets found</p>
            )}
          </div>
        </div>

        {/* System Overview */}
        <div className="glass-card p-6 animate-scale-in">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-success-500/20 rounded-lg flex items-center justify-center mr-3">
              <span className="text-success-600 text-sm">📊</span>
            </div>
            <h3 className="text-xl font-semibold text-neutral-800">System Overview</h3>
          </div>
          <div className="space-y-4">
            <OverviewItem label="Assets Available" value={availableAssets} color="text-success-600" />
            <OverviewItem label="Assets in Use" value={inUseAssets} color="text-info-600" />
            <OverviewItem label="Under Maintenance" value={maintenanceAssets} color="text-warning-600" />
            <OverviewItem label="Depreciated Assets" value={depreciatedAssets} color="text-error-600" />
          </div>
        </div>
      </div>

      {/* Quick Actions Section */}
      <div className="glass-card p-6 animate-fade-in mb-6">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center mr-3">
            <span className="text-purple-600 text-sm">⚡</span>
          </div>
          <h3 className="text-xl font-semibold text-neutral-800">Quick Actions</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button
            onClick={() => navigate("/admin/asset-manager")}
            className="p-4 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-lg hover:from-green-600 hover:to-green-700 transition-all duration-200 transform hover:scale-105"
          >
            <div className="text-2xl mb-2">💻</div>
            <div className="font-semibold">Asset Manager</div>
            <div className="text-sm opacity-90">Manage all assets</div>
          </button>
          <button
            onClick={() => navigate("/admin/user-administration")}
            className="p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all duration-200 transform hover:scale-105"
          >
            <div className="text-2xl mb-2">👥</div>
            <div className="font-semibold">User Admin</div>
            <div className="text-sm opacity-90">Manage users & roles</div>
          </button>
        </div>
      </div>

      {/* 🔥 Pending Requests Section */}
      <div className="glass-card p-4 md:p-6 animate-fade-in">
        <div className="flex items-center mb-4">
          <div className="w-8 h-8 bg-warning-500/20 rounded-lg flex items-center justify-center mr-3">
            <span className="text-warning-600 text-sm">📋</span>
          </div>
          <h3 className="text-lg md:text-xl font-semibold text-neutral-800">Pending Requests</h3>
        </div>
        {pendingRequests === 0 ? (
          <div className="text-center py-8 md:py-12">
            <div className="text-4xl md:text-6xl mb-4">🎉</div>
            <p className="text-neutral-500 text-base md:text-lg">No pending requests</p>
            <p className="text-neutral-400 text-sm">All caught up!</p>
          </div>
        ) : (
          <>
            {/* Mobile: Card Layout */}
            <div className="md:hidden space-y-3 mb-4">
              {requests
                .filter((r) => r.status === "pending")
                .slice(0, 5)
                .map((req) => (
                  <div key={req.id} className="bg-white/60 rounded-lg p-4 border border-surface-200">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <div className="font-medium text-neutral-800 text-sm mb-1">
                          {req.assetName || req.assetId}
                        </div>
                        <div className="text-xs text-neutral-500">
                          ID: {req.id}
                        </div>
                      </div>
                      <span className="px-2 py-1 bg-warning-100 text-warning-800 rounded-full text-xs font-medium">
                        {req.status}
                      </span>
                    </div>
                    <div className="text-xs text-neutral-600">
                      Requested by: {req.requestedBy || "Unknown"}
                    </div>
                  </div>
                ))}
            </div>

            {/* Desktop: Table Layout */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="border-b border-surface-300">
                    <th className="text-left py-3 px-4 font-semibold text-neutral-700">Request ID</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-700">Requested By</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-700">Asset</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {requests
                    .filter((r) => r.status === "pending")
                    .slice(0, 5)
                    .map((req) => (
                      <tr key={req.id} className="hover:bg-surface-100/50 transition-colors duration-200">
                        <td className="py-3 px-4 font-medium text-neutral-800">{req.id}</td>
                        <td className="py-3 px-4 text-neutral-600">{req.requestedBy || "Unknown"}</td>
                        <td className="py-3 px-4 text-neutral-600">{req.assetName || req.assetId}</td>
                        <td className="py-3 px-4">
                          <span className="px-3 py-1 bg-warning-100 text-warning-800 rounded-full text-xs font-medium">
                            {req.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* View All Button */}
            {pendingRequests > 5 && (
              <div className="mt-4 text-center">
                <button
                  onClick={() => navigate("/admin/request-approval")}
                  className="bg-primary-50 text-primary-600 hover:bg-primary-100 hover:text-primary-800 px-4 py-2 rounded-lg font-medium transition-colors duration-200"
                >
                  <span className="md:hidden">View all requests</span>
                  <span className="hidden md:inline">View all {pendingRequests} pending requests</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Admin Notifications */}
      {(overdueItems.length > 0 || notifications.length > 0) && (
        <div className="glass-card p-6 animate-fade-in mt-6">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center mr-3">
              <span className="text-red-600 text-sm">🔔</span>
            </div>
            <h3 className="text-xl font-semibold text-neutral-800">Admin Notifications</h3>
          </div>

          <div className="space-y-3">
            {/* Overdue Items */}
            {overdueItems.map((item) => (
              <div key={item.id} className="flex items-center justify-between p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <span className="text-red-600">⚠️</span>
                  <div>
                    <p className="font-medium text-red-900">
                      Overdue: {item.assetName}
                    </p>
                    <p className="text-sm text-red-700">
                      Borrowed by {item.borrower} • {item.daysOverdue} days overdue
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/admin/request-approval")}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                >
                  Review
                </button>
              </div>
            ))}

            {/* Depreciation Notifications */}
            {notifications.map((notif) => (
              <div key={notif.id} className={`flex items-center justify-between p-4 border rounded-lg ${
                notif.type === "depreciated"
                  ? "bg-red-50 border-red-200"
                  : "bg-yellow-50 border-yellow-200"
              }`}>
                <div className="flex items-center space-x-3">
                  <span className={notif.type === "depreciated" ? "text-red-600" : "text-yellow-600"}>
                    {notif.type === "depreciated" ? "⚠️" : "🔔"}
                  </span>
                  <div>
                    <p className={`font-medium ${
                      notif.type === "depreciated" ? "text-red-900" : "text-yellow-900"
                    }`}>
                      {notif.type === "depreciated" ? "Depreciated" : "Nearing Depreciation"}: {notif.assetName}
                    </p>
                    <p className={`text-sm ${
                      notif.type === "depreciated" ? "text-red-700" : "text-yellow-700"
                    }`}>
                      {notif.type === "depreciated"
                        ? `Depreciated on ${notif.depreciationDate.toLocaleDateString()}`
                        : `${notif.daysUntilDepreciation} days until depreciation`
                      }
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => navigate("/admin/asset-manager")}
                  className={`px-3 py-1 text-sm rounded ${
                    notif.type === "depreciated"
                      ? "bg-red-600 text-white hover:bg-red-700"
                      : "bg-yellow-600 text-white hover:bg-yellow-700"
                  }`}
                >
                  Manage
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// 💡 Small UI helper components
function StatCard({ title, value, icon, bgColor }) {
  return (
    <div className={`bg-gradient-to-r ${bgColor} rounded-2xl shadow-lg p-6 text-white backdrop-blur-sm border border-white/20 animate-scale-in`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold opacity-90">{title}</h3>
        <span className="text-2xl">{icon}</span>
      </div>
      <p className="text-5xl font-bold">{value}</p>
    </div>
  );
}

function OverviewItem({ label, value, color = "text-neutral-800" }) {
  return (
    <div className="flex justify-between items-center p-3 bg-surface-100/50 rounded-lg">
      <span className="text-neutral-700">{label}</span>
      <span className={`font-bold text-lg ${color}`}>{value}</span>
    </div>
  );
}
